import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, PaperPlaneTilt, UploadSimple, FileText, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import PhoneInput from '@/components/PhoneInput';

import { API } from '@/config/api';

const STATUS_OPTIONS = [
  { value: 'study_permit', label: 'Study Permit' },
  { value: 'work_permit', label: 'Work Permit' },
  { value: 'visitor', label: 'Visitor' },
  { value: 'pr', label: 'Permanent Resident' },
  { value: 'citizen', label: 'Canadian Citizen' },
  { value: 'refugee', label: 'Refugee / Protected Person' },
  { value: 'other', label: 'Other' },
];

const DOC_TYPES = [
  { value: 'class_12_marksheet', label: 'Class 12th Marksheet' },
  { value: 'class_12_certificate', label: 'Class 12th Certificate' },
  { value: 'passport', label: 'Passport Copy' },
  { value: 'pr_card_or_dl', label: 'PR Card / Driving License' },
  { value: 'english_test_score', label: 'English Test Score (optional)' },
  { value: 'other', label: 'Other Supporting Document' },
];

const EXAM_TYPES = ['CLBPT', 'ACCUPLACER', 'iTEP', 'IELTS'];

export default function StudentApply() {
  const [step, setStep] = useState(1);
  const [appId, setAppId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedDocType, setSelectedDocType] = useState('class_12_marksheet');
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', phone_code: '+1', dob: '',
    nationality: '', passport_pr_status: '',
    needs_exam_prep: false, exam_prep_types: [],
  });
  const [referenceNumber, setReferenceNumber] = useState(null);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const toggleExam = (type) => setForm(prev => ({ ...prev, exam_prep_types: prev.exam_prep_types.includes(type) ? prev.exam_prep_types.filter(t => t !== type) : [...prev.exam_prep_types, type] }));

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    const missing = [];
    if (!form.full_name.trim()) missing.push('Full Name');
    if (!form.phone.trim()) missing.push('Phone');
    if (!form.email.trim()) missing.push('Email');
    if (!form.dob) missing.push('Date of Birth');
    if (!form.nationality.trim()) missing.push('Nationality');
    if (!form.passport_pr_status) missing.push('Status in Canada');
    if (missing.length) { toast.error(`Please fill: ${missing.join(', ')}`); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        phone: form.phone ? `${form.phone_code} ${form.phone}` : '',
      };
      delete payload.phone_code;
      const { data } = await axios.post(`${API}/student-applications`, payload);
      setAppId(data.application.id);
      setReferenceNumber(data.reference_number);
      setStep(2);
      toast.success('Application saved! Now upload your documents.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !appId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', selectedDocType);
      const { data } = await axios.post(`${API}/student-applications/${appId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadedFiles(prev => [...prev, { ...data, doc_type_label: DOC_TYPES.find(d => d.value === selectedDocType)?.label || selectedDocType }]);
      toast.success('Document uploaded!');
    } catch {
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleFinish = async () => {
    try {
      if (appId) {
        await axios.patch(`${API}/student-applications/${appId}`, {
          needs_exam_prep: form.needs_exam_prep,
          exam_prep_types: form.exam_prep_types,
        }).catch(() => {});
      }
    } catch { /* ignore */ }
    setStep(3);
  };

  // ===== Success =====
  if (step === 3) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 bg-gradient-to-br from-blue-50 via-white to-amber-50" data-testid="apply-success">
        <div className="text-center max-w-lg">
          <CheckCircle size={64} weight="fill" className="text-emerald-500 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Libre Baskerville', serif" }}>Application Submitted!</h2>
          {referenceNumber && (
            <div className="bg-white border border-blue-100 rounded-lg p-4 mb-4 shadow-sm" data-testid="reference-number-display">
              <p className="text-xs text-blue-600 font-medium uppercase tracking-wider mb-1">Your Reference Number</p>
              <p className="text-2xl font-bold text-blue-800 font-mono">{referenceNumber}</p>
              <p className="text-xs text-gray-500 mt-1">Save this to track your application status</p>
            </div>
          )}
          <p className="text-gray-600 mb-2">Our admissions team will review your application and contact you within 2-3 business days.</p>
          {uploadedFiles.length > 0 && (<p className="text-sm text-gray-400 mb-4">{uploadedFiles.length} document(s) uploaded.</p>)}
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <Link to="/track-application" className="inline-flex items-center gap-2 bg-blue-700 text-white px-6 py-3 rounded-md font-medium hover:bg-blue-800 transition-all" data-testid="track-btn">Track Application</Link>
            <Link to="/" className="inline-flex items-center gap-2 border border-gray-300 text-gray-700 px-6 py-3 rounded-md font-medium hover:bg-gray-100 transition-all" data-testid="back-home-btn">Back to Home <ArrowRight size={18} /></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="student-apply-page">
      {/* Header */}
      <section className="py-14 bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-blue-600 font-medium text-sm uppercase tracking-widest mb-3">Student Application</p>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight mb-3" style={{ fontFamily: "'Libre Baskerville', serif" }}>Apply Now</h1>
            <p className="text-base lg:text-lg text-gray-600">A short form, then document upload. Takes less than 2 minutes.</p>
          </div>
        </div>
      </section>

      {/* Step indicator */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-2 ${step === 1 ? 'text-blue-700 font-semibold' : 'text-gray-400'}`}>
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step === 1 ? 'bg-blue-700 text-white' : 'bg-gray-200 text-gray-500'}`}>1</span>
              <span className="text-sm">Your Details</span>
            </div>
            <div className="w-8 h-px bg-gray-200" />
            <div className={`flex items-center gap-2 ${step === 2 ? 'text-blue-700 font-semibold' : 'text-gray-400'}`}>
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step === 2 ? 'bg-blue-700 text-white' : 'bg-gray-200 text-gray-500'}`}>2</span>
              <span className="text-sm">Upload Documents</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Step 1: Minimal Form */}
          {step === 1 && (
            <form onSubmit={handleSubmitForm} className="space-y-6" data-testid="apply-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input id="full_name" value={form.full_name} onChange={e => handleChange('full_name', e.target.value)} required data-testid="apply-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" value={form.email} onChange={e => handleChange('email', e.target.value)} required data-testid="apply-email-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <PhoneInput
                    id="phone"
                    value={form.phone}
                    onChange={e => handleChange('phone', e.target.value)}
                    countryCode={form.phone_code}
                    onCountryCodeChange={v => handleChange('phone_code', v)}
                    data-testid="apply-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dob">Date of Birth *</Label>
                  <Input id="dob" type="date" value={form.dob} onChange={e => handleChange('dob', e.target.value)} required data-testid="apply-dob-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="nationality">Nationality *</Label>
                  <Input id="nationality" value={form.nationality} onChange={e => handleChange('nationality', e.target.value)} required data-testid="apply-nationality-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status in Canada *</Label>
                  <Select value={form.passport_pr_status} onValueChange={v => handleChange('passport_pr_status', v)}>
                    <SelectTrigger id="status" data-testid="apply-status-select"><SelectValue placeholder="Select status" /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(o => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-blue-700 hover:bg-blue-800 text-white h-12" data-testid="apply-submit-btn">
                <PaperPlaneTilt size={18} className="mr-2" />
                {loading ? 'Submitting...' : 'Continue to Document Upload'}
              </Button>
            </form>
          )}

          {/* Step 2: Document Upload + English test */}
          {step === 2 && (
            <div className="space-y-6" data-testid="apply-upload-section">
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-5">
                <h3 className="text-base font-semibold text-gray-900 mb-1" style={{ fontFamily: "'Libre Baskerville', serif" }}>Upload Your Documents</h3>
                <p className="text-sm text-gray-600">Upload whatever documents you have ready. You can add more later or skip and submit now.</p>
              </div>

              {/* English test help */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-5" data-testid="english-help-section">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="need-english-help"
                    checked={form.needs_exam_prep}
                    onCheckedChange={(v) => handleChange('needs_exam_prep', !!v)}
                    data-testid="english-help-checkbox"
                  />
                  <Label htmlFor="need-english-help" className="cursor-pointer text-gray-900 font-medium">I need help with an English test</Label>
                </div>
                {form.needs_exam_prep && (
                  <>
                    <p className="text-xs text-gray-600 mt-2">Pick one or more exams you'd like prep support for:</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {EXAM_TYPES.map(t => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => toggleExam(t)}
                          className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${form.exam_prep_types.includes(t) ? 'bg-amber-100 border-amber-400 text-amber-900 font-medium' : 'bg-white border-gray-200 text-gray-600 hover:border-amber-300'}`}
                          data-testid={`english-exam-${t.toLowerCase()}`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                    <Button type="button" size="sm" variant="ghost" onClick={async () => {
                      try {
                        await axios.patch(`${API}/student-applications/${appId}`, { needs_exam_prep: form.needs_exam_prep, exam_prep_types: form.exam_prep_types }).catch(() => {});
                        toast.success('English prep preferences saved');
                      } catch {
                        /* silent - will still be captured at submit */
                      }
                    }} className="mt-2 text-xs text-amber-800 hover:text-amber-900">Save preferences</Button>
                  </>
                )}
              </div>

              {/* Upload control */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex flex-wrap items-end gap-3 mb-5">
                  <div className="space-y-2 flex-1 min-w-[200px]">
                    <Label>Document Type</Label>
                    <Select value={selectedDocType} onValueChange={setSelectedDocType}>
                      <SelectTrigger data-testid="apply-doc-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DOC_TYPES.map(dt => (<SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="apply-file-upload" className="cursor-pointer">
                      <div className="inline-flex items-center gap-2 bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-800 transition-colors">
                        <UploadSimple size={16} />
                        {uploading ? 'Uploading...' : 'Choose & Upload File'}
                      </div>
                    </Label>
                    <input id="apply-file-upload" type="file" className="hidden" onChange={handleFileUpload} disabled={uploading} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" data-testid="apply-file-input" />
                  </div>
                </div>

                {uploadedFiles.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Uploaded Documents ({uploadedFiles.length})</p>
                    {uploadedFiles.map(f => (
                      <div key={f.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-testid={`uploaded-file-${f.id}`}>
                        <FileText size={18} className="text-blue-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{f.original_filename}</p>
                          <p className="text-xs text-gray-500">{f.doc_type_label}</p>
                        </div>
                        <CheckCircle size={16} weight="fill" className="text-green-500 shrink-0" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-4">No documents uploaded yet</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3 justify-end">
                <Button variant="outline" onClick={handleFinish} data-testid="apply-skip-upload-btn">
                  {uploadedFiles.length > 0 ? 'Submit Application' : 'Skip & Submit Later'}
                </Button>
                {uploadedFiles.length > 0 && (
                  <Button onClick={handleFinish} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="apply-finish-btn">
                    <CheckCircle size={16} className="mr-2" /> Submit Application
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
